import { motion } from "framer-motion";
import { BadgeCheck, Facebook, Instagram, Languages, Mail, MapPin, Phone, TrendingUp } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const MonogramTile = ({ letter }) => (
  <div className="flex h-20 w-20 shrink-0 items-center justify-center bg-navy outline outline-1 outline-offset-4 outline-gold/50">
    <span className="font-serif text-3xl font-bold text-paper">{letter}</span>
  </div>
);

const AgentSpotlight = () => {
  const { t } = useLang();

  const agents = [
    {
      testid: "agent-card-ony",
      img: "/agents/ony.jpg",
      name: "Ony Valdivieso",
      role: t("tm.onyRole"),
      rows: [
        { icon: Phone, text: "(702) 241-4761", href: "tel:+17022414761" },
        { icon: Mail, text: "onyrealtor@gmail.com", href: "mailto:onyrealtor@gmail.com" },
        { icon: Languages, text: "English · Español" },
        { icon: TrendingUp, text: t("tm.sales"), mono: true },
        { icon: BadgeCheck, text: "Nevada Lic. #S.0061852", mono: true },
        { icon: Facebook, text: "facebook.com/onyrealtor", href: "https://www.facebook.com/onyrealtor/", external: true },
        { icon: Instagram, text: "@onyvaldivieso_realtor", href: "https://www.instagram.com/onyvaldivieso_realtor/", external: true },
      ],
    },
    {
      testid: "agent-card-ace",
      img: "/agents/ace.jpg",
      name: "Ace Edwards",
      role: t("tm.aceRole"),
      rows: [
        { icon: Phone, text: "702 · 613 · 7669", href: "tel:+17026137669" },
        { icon: Mail, text: "AceEdwardsLV@gmail.com", href: "mailto:AceEdwardsLV@gmail.com" },
        { icon: MapPin, text: "1325 S Eastern Ave, Las Vegas, NV 89104" },
        { icon: BadgeCheck, text: "Nevada Lic. #S.0200120", mono: true },
        { icon: Instagram, text: "@AceEdwardsLV", href: "https://www.instagram.com/AceEdwardsLV/", external: true },
      ],
    },
    {
      testid: "agent-card-nayky",
      img: "/agents/nayky.jpg",
      name: "Nayky Rankin Martino",
      role: t("tm.naykyRole"),
      rows: [
        { icon: Phone, text: "702 · 929 · 6263", href: "tel:+17029296263" },
        { icon: MapPin, text: "1325 S Eastern Ave, Las Vegas, NV 89104" },
        { icon: BadgeCheck, text: "Nevada Lic. #S.0200905", mono: true },
      ],
    },
  ];

  return (
    <section id="team" data-testid="team-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mx-auto max-w-2xl text-center"
      >
        <p className="eyebrow">{t("tm.eyebrow")}</p>
        <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-navy sm:text-4xl">
          {t("tm.h2")}
        </h2>
      </motion.div>

      <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {agents.map((a, i) => (
          <motion.div
            key={a.testid}
            data-testid={a.testid}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="engraved-frame bg-white p-8 shadow-elevated"
          >
            <div className="flex items-center gap-5">
              {a.img ? (
                <img
                  src={a.img}
                  alt={a.name}
                  className="h-20 w-20 shrink-0 border border-engraved object-cover"
                />
              ) : (
                <MonogramTile letter={a.monogram} />
              )}
              <div>
                <p className="font-serif text-xl font-bold tracking-wide text-navy sm:text-2xl">{a.name}</p>
                <p className="mt-1.5 font-cinzel text-[10px] font-semibold uppercase tracking-[0.25em] text-crimson">
                  {a.role}
                </p>
              </div>
            </div>
            <div className="mt-8 space-y-3.5 border-t border-engraved pt-7">
              {a.rows.map((r) => (
                <div key={r.text} className="flex items-center gap-3.5 text-sm text-ink">
                  <r.icon size={15} className="shrink-0 text-gold" />
                  {r.href ? (
                    <a href={r.href} {...(r.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="transition-colors hover:text-crimson">{r.text}</a>
                  ) : (
                    <span className={r.mono ? "font-mono text-xs" : ""}>{r.text}</span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default AgentSpotlight;
